import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Users, BookOpen, BarChart3, Calendar, MessageSquare, Settings, LogOut, Plus, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import PadaidLogo from '../../components/ui/PadaidLogo';

const CoFounderDashboard = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-accent/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground">Espace</span>
                  <Badge variant="secondary" className="ml-2 bg-accent text-accent-foreground">
                    <Star className="w-3 h-3 mr-1" />
                    Co-fondateur
                  </Badge>
                </div>
                <h1 className="text-lg font-semibold text-accent">Tableau de Bord Étendu</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Paramètres
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Espace Co-fondateur
          </h2>
          <p className="text-muted-foreground">
            Gérez et supervisez les activités PADAID avec des privilèges étendus
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Articles Publiés</CardTitle>
              <BookOpen className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">24</div>
              <p className="text-xs text-muted-foreground">+2 ce mois</p>
            </CardContent>
          </Card>

          <Card className="border-secondary/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Membres Actifs</CardTitle>
              <Users className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">156</div>
              <p className="text-xs text-muted-foreground">+12 ce mois</p>
            </CardContent>
          </Card>

          <Card className="border-accent/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Projets Actifs</CardTitle>
              <BarChart3 className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">8</div>
              <p className="text-xs text-muted-foreground">3 en cours</p>
            </CardContent>
          </Card>

          <Card className="border-destructive/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Dons du Mois</CardTitle>
              <BarChart3 className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">€2,430</div>
              <p className="text-xs text-muted-foreground">+18% vs. dernier mois</p>
            </CardContent>
          </Card>
        </div>

        {/* Management Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow border-primary/20">
            <CardHeader className="text-center">
              <Plus className="h-8 w-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Créer Article</CardTitle>
              <CardDescription>Rédiger un nouvel article ou tract</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-primary hover:bg-primary/90">
                Nouveau contenu
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-secondary/20">
            <CardHeader className="text-center">
              <Users className="h-8 w-8 text-secondary mx-auto mb-2" />
              <CardTitle className="text-lg">Gestion Membres</CardTitle>
              <CardDescription>Superviser les adhésions et inscriptions</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-secondary hover:bg-secondary/90 text-black">
                Gérer membres
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-accent/20">
            <CardHeader className="text-center">
              <Calendar className="h-8 w-8 text-accent mx-auto mb-2" />
              <CardTitle className="text-lg">Événements</CardTitle>
              <CardDescription>Planifier et organiser les événements</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-accent hover:bg-accent/90">
                Planifier événement
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-destructive/20">
            <CardHeader className="text-center">
              <MessageSquare className="h-8 w-8 text-destructive mx-auto mb-2" />
              <CardTitle className="text-lg">Messages</CardTitle>
              <CardDescription>Communications avec la communauté</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-destructive hover:bg-destructive/90">
                Centre messages
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-blue-500/20">
            <CardHeader className="text-center">
              <BarChart3 className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Rapports</CardTitle>
              <CardDescription>Analyses et statistiques détaillées</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-blue-500 hover:bg-blue-600">
                Voir rapports
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-green-500/20">
            <CardHeader className="text-center">
              <Edit className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Site Web</CardTitle>
              <CardDescription>Modifier le contenu du site</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-green-500 hover:bg-green-600">
                Éditer contenu
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity & Privileges */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Star className="mr-2 h-5 w-5 text-accent" />
                Privilèges Co-fondateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Gestion complète du contenu éditorial</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Supervision des membres et adhésions</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Organisation d'événements et rencontres</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Accès aux rapports financiers</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Modification du contenu du site web</span>
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span>Communication avec les partenaires</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activité Récente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Article publié</p>
                    <p className="text-xs text-muted-foreground">Rapport sur la mission au Mali</p>
                    <p className="text-xs text-muted-foreground">Il y a 3 heures</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-secondary rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Nouveau membre validé</p>
                    <p className="text-xs text-muted-foreground">Marie Dubois - Plan Supporter</p>
                    <p className="text-xs text-muted-foreground">Il y a 6 heures</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Événement créé</p>
                    <p className="text-xs text-muted-foreground">Conférence sur l'aide humanitaire</p>
                    <p className="text-xs text-muted-foreground">Il y a 1 jour</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium">Don important reçu</p>
                    <p className="text-xs text-muted-foreground">500€ pour le projet éducation</p>
                    <p className="text-xs text-muted-foreground">Il y a 2 jours</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Access */}
        <Card className="mt-8 bg-accent/5 border-accent/20">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2 flex items-center justify-center">
                <Star className="mr-2 h-5 w-5 text-accent" />
                Accès Administration
              </h3>
              <p className="text-muted-foreground mb-4">
                En tant que Co-fondateur, vous avez accès au panneau d'administration complet
              </p>
              <Button className="bg-accent hover:bg-accent/90" asChild>
                <Link to="/admin">Panneau Admin</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoFounderDashboard;